import { InnerBox } from "@/components/elements/InnerBox";
import { TopPage } from "@/components/elements/TopPage";
import { CreatePartnerUserForm, CreatePartnerUserFormRef } from "@/features/rso/components/CreatePartnerUserForm";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRef } from "react";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { styled } from "@mui/system";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { text, background } from "@/theme/colors";
import { IconSend } from "@/assets/icons";

type TPartnerUserCreatePageProps = {};

export const PartnerUserCreatePage: FC<TPartnerUserCreatePageProps> = () => {
  const formRef = useRef<CreatePartnerUserFormRef>(null);
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await Promise.resolve(formRef.current?.submit());
    } finally {
      setLoading(false);
    }
  };

  return (
    <PartnerWrapper sx={{ mt: 1 }}>
      <Box sx={{ mb: 2.5 }}>
        <Stack gap={4}>
          <TopPage
            backUrl={getDynamicRoute(AppRoutes.rsoDetail, { id })}
            title="新規顧客ユーザーの追加"
            description="顧客側画面にログインできるユーザーを新しく作成し、パスワードの新規登録リンクを含むログイン情報をメールアドレス宛に送付します。"
            enablePageBack={false}
          />
          <MenuNavigate
            contents={[
              {
                title: "ユーザーの状態",
                content: (
                  <InnerBox title="ユーザーの状態">
                    <Grid container columnSpacing={3} rowSpacing={2} alignItems="flex-end">
                      <Grid item xs={5}>
                        <Stack gap={0.5}>
                          <Typography variant="cap12" color={text.secondary}>
                            ステータス *
                          </Typography>
                          <Typography variant="body14" color={text.primary}>
                            使用中
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={4}>
                        <Stack gap={0.5}>
                          <Typography variant="cap12" color={text.secondary}>
                            認証ステータス
                          </Typography>
                          <Typography variant="body14" color={text.primary}>
                            登録メール送信前
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={3}>
                        <Button
                          size="sm-round"
                          variant="secondary"
                          disabled
                          sx={{
                            width: "100%",
                            border: "none !important",
                            backgroundColor: `${background.disable}`,
                            color: text.tertiary,
                            "&.Mui-disabled": {
                              border: "none !important",
                              backgroundColor: `${background.disable} !important`,
                              color: `${text.tertiary} !important`,
                            },
                          }}
                        >
                          パスワードリセット
                        </Button>
                      </Grid>
                    </Grid>
                  </InnerBox>
                ),
              },
              {
                title: "基本情報",
                content: (
                  <InnerBox title="契約情報">
                    <CreatePartnerUserForm ref={formRef} />
                  </InnerBox>
                ),
              },
            ]}
            contentWidth="900px"
            contentAffix={
              <FooterContainer>
                <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                  <Button size="sm-round" variant="secondary" onClick={() => navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }))}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={3}>
                    <Button
                      disabled={loading}
                      onClick={handleSubmit}
                      size="sm"
                      startIcon={<IconSend />}
                      sx={{
                        "& .MuiButton-startIcon svg path": {
                          fill: "currentColor",
                        },
                      }}
                    >
                      メールを送信
                    </Button>
                  </Stack>
                </Stack>
              </FooterContainer>
            }
          />
        </Stack>
      </Box>
    </PartnerWrapper>
  );
};

const PartnerWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
`;
